import { NodeType } from "../../../types/NodeType";
import { coercePortValue, normalizeVariaveisList } from "../../../lib/util";
import { REENGAJAMENTO_CFG_PORT_NAME } from "../../reengajamento";

export const TRIGGER_TYPE_OPTIONS: Record<string, string> = {
  canal: "Atendimento automático",
  campanha: "Campanha",
  agendado: "Disparo agendado",
  retomar: "Retomar atendimento",
  manual: "Disparo manual"
};

export type TriggerType = keyof typeof TRIGGER_TYPE_OPTIONS;

const outputRoute = { type: "route", name: "route", label: "Fluxo", description: "Início do fluxo de conversação" };

// Trigger types cujo nó Início permite declarar variáveis de entrada
// (lidas de context.params e expostas como ports var_<nome>)
const TRIGGER_TYPES_COM_VARIAVEIS: TriggerType[] = ["campanha", "manual"];

function resolverVariaveisDeclaradas(rawVariaveis: any[] | undefined, context: Record<string, any>) {
  const variaveis = normalizeVariaveisList(rawVariaveis ?? []);
  return variaveis.reduce<Record<string, any>>((acc, item) => {
    acc[`var_${item.nome}`] = coercePortValue(context.params?.[item.nome], item.tipo);
    return acc;
  }, {});
}
const outputIdCanalCliente = { type: "string", name: "idCanalCliente", label: "ID usuário", description: "Identificador único do usuário no canal" };
const outputNomeUsuarioCanal = { type: "string", name: "nomeUsuarioCanal", label: "Nome usuário", description: "Nome do usuário capturado no canal" };
const portDomainCanal = { type: "domainCanal", name: "domainCanal", label: "Canal de disparo", description: "Informações do canal que disparou o fluxo", hidePort: true };
const outputCanalId = { type: "number", name: "canal_id", label: "ID do canal", description: "ID do canal que disparou esta execução do fluxo (útil para repassar a um nó Novo fluxo de atendimento)" };

const instance: NodeType = {
  type: "start",
  label: "Início",
  description: "Inicia um fluxo de atendimento",
  root: true,
  inputs(inputValues) {
    const result: ReturnType<typeof instance.inputs> = [
      {
        type: "triggerType",
        name: "triggerType",
        label: "Tipo de trigger",
        hidePort: true,
        defaultValue: "canal",
      },
    ];

    if ((inputValues?.triggerType ?? "canal") === "agendado") {
      result.push({
        type: "triggerAgendamento",
        name: "agendamento",
        label: "Agendamento",
        hidePort: true,
        defaultValue: { modo: "fixo", agendas: [], considerarFeriados: false },
      });
    }

    if (TRIGGER_TYPES_COM_VARIAVEIS.includes((inputValues?.triggerType as TriggerType) ?? "canal")) {
      result.push({
        type: "variaveisCampanhaList",
        name: "variaveisCampanha",
        label: "Variáveis de entrada",
        hidePort: true,
        defaultValue: [] as { nome: string; tipo: string }[],
      });
    }

    // if (['canal', 'agendado'].includes(inputValues?.triggerType ?? "canal")) {
    result.push(portDomainCanal);
    // }

    return result;
  },
  outputs(inputValues) {
    const triggerType: TriggerType = (inputValues?.triggerType as TriggerType) ?? "canal";

    if (triggerType === "campanha") {
      const variaveisCampanha = normalizeVariaveisList(inputValues?.variaveisCampanha ?? []);
      return [
        outputIdCanalCliente,
        outputNomeUsuarioCanal,
        { type: "string", name: "nomeCampanha", label: "Nome da campanha", description: "Nome da campanha que disparou o fluxo" },
        { type: "string", name: "idCampanha", label: "ID da campanha", description: "Identificador da campanha" },
        ...variaveisCampanha.map((item) => ({
          type: item.tipo as any,
          name: `var_${item.nome}`,
          label: item.nome,
          description: `Variável de campanha: ${item.nome}`,
        })),
        portDomainCanal,
        outputCanalId,
        outputRoute,
      ];
    }

    if (triggerType === "agendado") {
      return [
        { type: "string", name: "horarioDisparo", label: "Horário do disparo", description: "Horário em que o fluxo foi disparado (ISO 8601)" },
        portDomainCanal,
        outputCanalId,
        outputRoute,
      ];
    }

    if (triggerType === "retomar") {
      return [
        outputIdCanalCliente,
        outputNomeUsuarioCanal,
        { type: "string", name: "mensagem", label: "Última mensagem", description: "Última mensagem recebida no atendimento" },
        portDomainCanal,
        outputCanalId,
        outputRoute,
      ];
    }

    if (triggerType === "manual") {
      const variaveisManual = normalizeVariaveisList(inputValues?.variaveisCampanha ?? []);
      return [
        outputIdCanalCliente,
        outputNomeUsuarioCanal,
        ...variaveisManual.map((item) => ({
          type: item.tipo as any,
          name: `var_${item.nome}`,
          label: item.nome,
          description: `Variável de entrada: ${item.nome}`,
        })),
        portDomainCanal,
        outputCanalId,
        outputRoute,
      ];
    }

    // canal (padrão)
    return [
      { type: "string", name: "tipoCanal", label: "Tipo canal", description: "O tipo de canal (ex: whatsapp, webchat)" },
      outputNomeUsuarioCanal,
      { type: "string", name: "mensagem", label: "Mensagem", description: "Conteúdo da mensagem inicial recebida" },
      outputIdCanalCliente,
      portDomainCanal,
      outputCanalId,
      outputRoute,
      {
        type: "cfg-reengajamento",
        name: REENGAJAMENTO_CFG_PORT_NAME,
        label: "Config. inatividade",
        description: "Ligue a um nó de Inatividade e reengajamento para configurar a inatividade do chatbot (rota sem cutucada com 0 tentativas, ou cutucada + botões com tentativas > 0). Substitui o antigo route-inatividade do Início.",
      },
    ];
  },
  async resolveOutputs(data, outputConnections, context) {
    const triggerType: TriggerType = (data?.triggerType as TriggerType) ?? "canal";

    if (triggerType === "campanha") {
      return {
        idCanalCliente: context.idCanalCliente ?? "",
        nomeUsuarioCanal: context.nomeUsuarioCanal ?? "",
        nomeCampanha: context.nomeCampanha ?? "",
        idCampanha: context.idCampanha != null ? String(context.idCampanha) : "",
        canal_id: context.canal?.id ?? null,
        ...resolverVariaveisDeclaradas(data?.variaveisCampanha, context),
      };
    }

    if (triggerType === "agendado") {
      return {
        horarioDisparo: context.horarioDisparo ?? new Date().toISOString(),
        canal_id: context.canal?.id ?? null,
      };
    }

    if (triggerType === "retomar") {
      return {
        idCanalCliente: context.idCanalCliente ?? "",
        nomeUsuarioCanal: context.nomeUsuarioCanal ?? "",
        mensagem: context.mensagem ?? "",
        canal_id: context.canal?.id ?? null,
      };
    }

    if (triggerType === "manual") {
      return {
        idCanalCliente: context.idCanalCliente ?? "",
        nomeUsuarioCanal: context.nomeUsuarioCanal ?? "",
        canal_id: context.canal?.id ?? null,
        ...resolverVariaveisDeclaradas(data?.variaveisCampanha, context),
      };
    }

    // canal (padrão)
    return {
      tipoCanal: context.canal?.tipo ?? "",
      nomeUsuarioCanal: context.nomeUsuarioCanal ?? "",
      mensagem: context.mensagem ?? "",
      idCanalCliente: context.idCanalCliente ?? "",
      canal_id: context.canal?.id ?? null,
    };
  },
  transcript(nodeId, inputValues, indent, nextNodeId, stack) {
    stack.push({ result: `[Início fluxo]`, indent });
    const next = nextNodeId(nodeId, "route");
    if (!next) return;

    stack.push({ id: next, indent });

    stack.push({ result: `[Fim fluxo]`, indent });
  },
};

export default instance;
